import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  Person as PersonIcon,
  CameraAlt as CameraIcon,
  DeleteOutline as DeleteOutlineIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(
          `${BASE_URL}/api/employees/logineduser/myprofile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch profile");
        }

        const data = await response.json();
        setProfile(data);
        setLoading(false);
      } catch (error) {
        setError(error.message || "Something went wrong");
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.match("image.*")) {
      setError("Please select an image file (JPEG, PNG)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File size should be less than 5MB");
      return;
    }

    setIsUploading(true);
    try {
      const token = sessionStorage.getItem("token");
      const formData = new FormData();
      formData.append("profilePicture", file);

      const response = await fetch(
        `${BASE_URL}/api/employees/myprofile/documents`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to upload profile picture"
        );
      }

      const data = await response.json();
      setProfile((prev) => ({ ...prev, profilePicture: data.profilePicture }));
      setError(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    setIsUploading(true);
    try {
      const token = sessionStorage.getItem("token");
      const formData = new FormData();
      formData.append("removeProfilePicture", "true");

      const response = await fetch(
        `${BASE_URL}/api/employees/myprofile/documents`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to remove profile picture"
        );
      }

      setProfile((prev) => ({ ...prev, profilePicture: "" }));
      setError(null);
    } catch (error) {
      setError(error.message || "Failed to remove profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  // Reusable label-value component
  const InfoItem = ({ label, value }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          color: "#8b8fa3",
          fontSize: "0.75rem",
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ color: "#1a1a2e", fontSize: "0.9375rem", fontWeight: 500 }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );

  // Section title component
  const SectionTitle = ({ children }) => (
    <Typography
      sx={{
        fontSize: "1rem",
        fontWeight: 600,
        color: "#1a1a2e",
        borderBottom: "2px solid #f0f0f5",
        pb: 1.5,
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );

  if (loading) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 12,
            gap: 2,
          }}
        >
          <CircularProgress size={28} sx={{ color: "#667eea" }} />
          <Typography sx={{ color: "#8b8fa3" }}>
            Loading Profile Information...
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 12,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: "center",
              borderRadius: "16px",
              border: "1px solid #eef0f6",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                bgcolor: "#FEE2E2",
                color: "#991B1B",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "16px",
                mx: "auto",
                fontSize: 24,
                fontWeight: 700,
                mb: 2,
              }}
            >
              !
            </Box>
            <Typography
              sx={{ fontSize: "1.125rem", fontWeight: 600, color: "#1a1a2e" }}
            >
              Profile Unavailable
            </Typography>
            <Typography sx={{ color: "#8b8fa3", mt: 1, fontSize: "0.875rem" }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{
                mt: 3,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 4,
                "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
              }}
            >
              Refresh Profile
            </Button>
          </Paper>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="My Profile"
        subtitle="View your personal details and employment information"
        icon={<PersonIcon />}
      />

      {/* Profile Header Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #eef0f6",
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2.5,
          }}
        >
          {/* Avatar with upload button */}
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Avatar
              alt={profile.name}
              src={profile.profilePicture || undefined}
              sx={{
                width: 80,
                height: 80,
                border: "3px solid #eef0f6",
                background: profile.profilePicture
                  ? undefined
                  : "linear-gradient(135deg, #667eea, #764ba2)",
                color: "#fff",
                fontWeight: 700,
              }}
            >
              {!profile.profilePicture
                ? (profile?.name?.charAt(0)?.toUpperCase() || "U")
                : null}
            </Avatar>
            <label htmlFor="profile-picture-upload">
              <input
                id="profile-picture-upload"
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                style={{ display: "none" }}
              />
              <Button
                variant="contained"
                component="span"
                size="small"
                disabled={isUploading}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  minWidth: 28,
                  width: 28,
                  height: 28,
                  p: 0,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "2px solid #fff",
                  "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
                }}
              >
                {isUploading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CameraIcon sx={{ fontSize: 14 }} />
                )}
              </Button>
            </label>
            {profile.profilePicture && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleRemoveProfilePicture}
                disabled={isUploading}
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  minWidth: 28,
                  width: 28,
                  height: 28,
                  p: 0,
                  borderRadius: "50%",
                  borderColor: "#FCA5A5",
                  color: "#B91C1C",
                  bgcolor: "#fff",
                  "&:hover": {
                    borderColor: "#DC2626",
                    bgcolor: "#FEF2F2",
                  },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </Button>
            )}
          </Box>

          {/* Name + Role + Key Info */}
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "1.25rem",
                fontWeight: 700,
                color: "#1a1a2e",
                mb: 0.25,
              }}
            >
              {profile.name}
            </Typography>
            <Typography
              sx={{ color: "#8b8fa3", fontSize: "0.875rem", mb: 1.5 }}
            >
              {profile.role}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 2, md: 4 },
              }}
            >
              {[
                {
                  icon: <BadgeIcon sx={{ fontSize: 16 }} />,
                  value: profile.employeeid,
                },
                {
                  icon: <EmailIcon sx={{ fontSize: 16 }} />,
                  value: profile.email,
                },
                {
                  icon: <PhoneIcon sx={{ fontSize: 16 }} />,
                  value: profile.contactNumber,
                },
                {
                  icon: <CalendarIcon sx={{ fontSize: 16 }} />,
                  value: profile.dateOfJoining
                    ? new Date(profile.dateOfJoining).toLocaleDateString(
                        "en-US",
                        { month: "short", day: "numeric", year: "numeric" }
                      )
                    : "N/A",
                },
              ].map((item, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                >
                  <Box sx={{ color: "#8b8fa3" }}>{item.icon}</Box>
                  <Typography sx={{ color: "#3d3f52", fontSize: "0.8125rem" }}>
                    {item.value || "N/A"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Detail Grid */}
      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid #eef0f6",
              bgcolor: "#fff",
              height: "100%",
            }}
          >
            <SectionTitle>Personal Information</SectionTitle>
            <Grid container spacing={0}>
              <Grid item xs={6}>
                <InfoItem label="Gender" value="N/A" />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label="Date of Birth" value="N/A" />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label="Nationality" value="Indian" />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label="Hometown" value="N/A" />
              </Grid>
              <Grid item xs={6}>
                <InfoItem
                  label="Reporting Manager"
                  value={profile.manager}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label="Religion" value="N/A" />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label="Work Mode" value={profile.workMode} />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label="Marital Status" value="N/A" />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Compensation */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid #eef0f6",
              bgcolor: "#fff",
              height: "100%",
            }}
          >
            <SectionTitle>Compensation</SectionTitle>
            <Grid container spacing={0}>
              <Grid item xs={6}>
                <InfoItem
                  label="Offered Salary"
                  value={
                    profile.offeredSalary
                      ? `₹${profile.offeredSalary}`
                      : "N/A"
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <InfoItem
                  label="TDS Amount"
                  value={
                    profile.TDSAmount ? `₹${profile.TDSAmount}` : "N/A"
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <InfoItem
                  label="Payment Mode"
                  value={profile.paymentMode || "Bank Transfer"}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label="Bank Name" value={profile.bankName} />
              </Grid>
              <Grid item xs={6}>
                <InfoItem
                  label="Account Number"
                  value={profile.accountNumber}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoItem
                  label="Job Location"
                  value={profile.jobLocation}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Documents */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid #eef0f6",
              bgcolor: "#fff",
            }}
          >
            <SectionTitle>Documents</SectionTitle>
            <Grid container spacing={0}>
              {profile.documents &&
                Object.entries(profile.documents).map(([key, value]) => (
                  <Grid item xs={6} key={key}>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          color: "#8b8fa3",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          mb: 0.5,
                        }}
                      >
                        {key.split(/(?=[A-Z])/).join(" ")}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#1a1a2e",
                          fontSize: "0.9375rem",
                          fontWeight: 500,
                        }}
                      >
                        {value ? (
                          <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#667eea",
                              textDecoration: "none",
                              fontWeight: 600,
                              fontSize: "0.875rem",
                            }}
                          >
                            View Document
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Address Information */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid #eef0f6",
              bgcolor: "#fff",
            }}
          >
            <SectionTitle>Address Information</SectionTitle>
            <Grid container spacing={0}>
              <Grid item xs={6}>
                <InfoItem label="Permanent Address" value="N/A" />
              </Grid>
              <Grid item xs={6}>
                <InfoItem label="Current Address" value="N/A" />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default UserProfile;
